import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import { CheckIcon, ChevronsUpDownIcon, XIcon } from 'lucide-react';

import { Icon } from '../icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/new/components/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

type MultiSelectContextType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedValues: Set<string>;
  toggleValue: (value: string) => void;
  clearAll: () => void;
  items: Map<string, ReactNode>;
  onItemAdded: (value: string, label: ReactNode) => void;
};
const MultiSelectContext = createContext<MultiSelectContextType | null>(null);

export function MultiSelect({
  children,
  values,
  defaultValues,
  onValuesChange,
}: {
  children: ReactNode;
  values?: string[];
  defaultValues?: string[];
  onValuesChange?: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [internalValues, setInternalValues] = useState(new Set<string>(values ?? defaultValues));
  const selectedValues = values ? new Set(values) : internalValues;
  const [items, setItems] = useState<Map<string, ReactNode>>(new Map());

  function toggleValue(value: string) {
    const getNewSet = (prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    };
    setInternalValues(getNewSet);
    onValuesChange?.([...getNewSet(selectedValues)]);
  }

  function clearAll() {
    setInternalValues(new Set());
    onValuesChange?.([]);
  }

  const onItemAdded = useCallback((value: string, label: ReactNode) => {
    setItems((prev) => {
      if (prev.get(value) === label) return prev;
      return new Map(prev).set(value, label);
    });
  }, []);

  return (
    <MultiSelectContext.Provider
      value={{
        open,
        setOpen,
        selectedValues,
        toggleValue,
        clearAll,
        items,
        onItemAdded,
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </MultiSelectContext.Provider>
  );
}

export function MultiSelectTrigger({
  className,
  children,
  ...props
}: {
  className?: string;
  children?: ReactNode;
} & ComponentPropsWithoutRef<typeof Button>) {
  const { open } = useMultiSelectContext();

  return (
    <PopoverTrigger asChild>
      <Button
        {...props}
        variant={props.variant ?? 'outline'}
        role={props.role ?? 'combobox'}
        aria-expanded={props['aria-expanded'] ?? open}
        className={cn(
          "focus-visible:border-ring aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg:not([class*='text-'])]:text-muted-foreground flex h-auto min-h-9 w-fit items-center justify-between gap-2 overflow-hidden rounded-md border bg-transparent p-1.5 whitespace-nowrap transition-colors duration-150 outline-none hover:bg-gray-50/50 focus-visible:ring focus-visible:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-gray-500 [&_span]:pl-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className,
        )}
      >
        {children}
        <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  );
}

export function MultiSelectValue({
  placeholder,
  clickToRemove = true,
  className,
  overflowBehavior = 'wrap-when-open',
  ...props
}: {
  placeholder?: string;
  clickToRemove?: boolean;
  overflowBehavior?: 'wrap' | 'wrap-when-open' | 'cutoff';
} & Omit<ComponentPropsWithoutRef<'div'>, 'children'>) {
  const { selectedValues, toggleValue, items, open } = useMultiSelectContext();
  const [overflowAmount, setOverflowAmount] = useState(0);
  const valueRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const shouldWrap = overflowBehavior === 'wrap' || (overflowBehavior === 'wrap-when-open' && open);

  const checkOverflow = useCallback(() => {
    if (valueRef.current == null) return;

    // Wrap in requestAnimationFrame to avoid ResizeObserver loop warnings.
    // DOM/state changes during ResizeObserver callbacks can trigger additional
    // resize events, causing the "loop completed with undelivered notifications" error.
    requestAnimationFrame(() => {
      if (valueRef.current == null) return;

      const containerElement = valueRef.current;
      const overflowElement = overflowRef.current;
      const items = containerElement.querySelectorAll<HTMLElement>('[data-selected-item]');

      if (overflowElement != null) overflowElement.style.display = 'none';
      items.forEach((child) => child.style.removeProperty('display'));
      let amount = 0;
      for (let i = items.length - 1; i >= 0; i--) {
        const child = items[i]!;
        if (containerElement.scrollWidth <= containerElement.clientWidth) {
          break;
        }
        amount = items.length - i;
        child.style.display = 'none';
        overflowElement?.style.removeProperty('display');
      }
      setOverflowAmount(amount);
    });
  }, []);

  useEffect(() => {
    const node = valueRef.current;
    if (!node) return;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    const observer = new ResizeObserver(checkOverflow);
    observerRef.current = observer;
    observer.observe(node);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [checkOverflow, valueRef.current]);

  if (selectedValues.size === 0 && placeholder) {
    return <span className="min-w-0 overflow-hidden font-normal text-gray-500">{placeholder}</span>;
  }

  return (
    <div
      {...props}
      ref={valueRef}
      className={cn('flex w-fit gap-1.5 overflow-hidden', shouldWrap && 'h-full flex-wrap')}
    >
      {[...selectedValues]
        .filter((value) => items.has(value))
        .map((value) => (
          <Badge
            variant="outline"
            data-selected-item
            className={cn(
              'group flex items-center gap-1 rounded-md bg-gray-50 duration-150',
              clickToRemove && 'hover:border-red-300 hover:bg-red-50 hover:text-red-500',
              className,
            )}
            key={value}
            onClick={
              clickToRemove
                ? (e) => {
                    e.stopPropagation();
                    toggleValue(value);
                  }
                : undefined
            }
          >
            {items.get(value)}
            {clickToRemove && <XIcon className="text-muted-foreground group-hover:text-destructive -mr-0.75 size-2" />}
          </Badge>
        ))}
      <div
        style={{
          display: overflowAmount > 0 && !shouldWrap ? 'block' : 'none',
        }}
        ref={overflowRef}
      >
        <Badge variant="outline">+{overflowAmount}</Badge>
      </div>
    </div>
  );
}

export function MultiSelectContent({
  search = true,
  children,
  showClearAll = true,
  ...props
}: {
  search?: boolean | { placeholder?: string; emptyMessage?: string };
  showClearAll?: boolean;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<typeof Command>, 'children'>) {
  const canSearch = typeof search === 'object' ? true : search;

  return (
    <>
      <div className="bg-white" style={{ display: 'none' }}>
        <Command>
          <CommandList>{children}</CommandList>
        </Command>
      </div>
      <PopoverContent className="min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command {...props}>
          {canSearch ? (
            <CommandInput placeholder={typeof search === 'object' ? search.placeholder : undefined} />
          ) : (
            <button autoFocus className="sr-only" />
          )}
          <CommandList>
            {canSearch && <CommandEmpty>{typeof search === 'object' ? search.emptyMessage : undefined}</CommandEmpty>}
            {showClearAll && <MultiSelectClearAll />}
            {children}
          </CommandList>
        </Command>
      </PopoverContent>
    </>
  );
}

export function MultiSelectItem({
  value,
  children,
  badgeLabel,
  onSelect,
  ...props
}: {
  badgeLabel?: ReactNode;
  value: string;
} & Omit<ComponentPropsWithoutRef<typeof CommandItem>, 'value'>) {
  const { toggleValue, selectedValues, onItemAdded } = useMultiSelectContext();
  const isSelected = selectedValues.has(value);

  useEffect(() => {
    onItemAdded(value, badgeLabel ?? children);
  }, [value, children, onItemAdded, badgeLabel]);

  return (
    <CommandItem
      className="cursor-pointer hover:bg-gray-50"
      {...props}
      onSelect={() => {
        toggleValue(value);
        onSelect?.(value);
      }}
    >
      <CheckIcon className={cn('mr-0.5 ml-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')} />
      {children}
    </CommandItem>
  );
}

export function MultiSelectClearAll() {
  const { selectedValues, clearAll } = useMultiSelectContext();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (selectedValues.size > 0) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [selectedValues.size]);

  if (selectedValues.size === 0) {
    return null;
  }

  return (
    <CommandItem
      className={cn(
        'text-destructive sticky top-0 z-10 cursor-pointer bg-white py-3 pl-4 text-gray-500 transition-all duration-100 ease-in-out hover:bg-gray-50',
        isVisible ? 'opacity-100' : 'opacity-0',
      )}
      onSelect={clearAll}
    >
      <Icon className="lucide--x mr-0.5" />
      Clear all ({selectedValues.size})
    </CommandItem>
  );
}

export function MultiSelectGroup(props: ComponentPropsWithoutRef<typeof CommandGroup>) {
  return <CommandGroup {...props} />;
}

export function MultiSelectSeparator(props: ComponentPropsWithoutRef<typeof CommandSeparator>) {
  return <CommandSeparator {...props} />;
}

function useMultiSelectContext() {
  const context = useContext(MultiSelectContext);
  if (context == null) {
    throw new Error('useMultiSelectContext must be used within a MultiSelectContext');
  }
  return context;
}
